import React, { useCallback, useEffect, useMemo } from "react";
import { Pagination } from "react-bootstrap";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";

import "./ShopScreen.css";

import { listProductsByShop } from "../../actions/productActions";
import Meta from "../../components/Meta";
import LuxFilterBar from "../../components/filteringComponent/LuxFilterBar";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import ProductCard from "../../components/ProductCard";
import { isValidShopKey, SHOPS } from "../../constants/shopMeta";
import { MTM_STEP_LABELS, normalizeMtmStep } from "../../utils/madeToMeasureJourney";
import { getServiceCatalogContext } from "../../utils/serviceCustomerCopy";

const PAGE_SIZE = 12;

const VALID_SORTS = new Set([
  "newest",
  "oldest",
  "price_asc",
  "price_desc",
  "name_asc",
  "name_desc",
]);

const EMPTY_FACETS = Object.freeze({
  colors: [],
  materials: [],
  sizes: [],
  patterns: [],
});

function parseCsvParam(value) {
  return [
    ...new Set(
      String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ];
}

function buildCsvParam(values = []) {
  return [
    ...new Set(
      values.map((value) => String(value || "").trim()).filter(Boolean),
    ),
  ].join(",");
}

function parsePositiveInt(value, fallback = 1) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return Math.floor(parsed);
}

function normalizePriceParam(value) {
  const text = String(value || "").trim();
  if (!text) return "";

  const parsed = Number(text);
  if (!Number.isFinite(parsed) || parsed < 0) return "";

  return String(parsed);
}

function normalizeIdParam(value) {
  return String(value || "").trim();
}

function normalizeSort(value) {
  const normalized = String(value || "").trim();
  return VALID_SORTS.has(normalized) ? normalized : "newest";
}

function cleanQueryParams(params) {
  const entries = Object.entries(params).filter(([, value]) => {
    if (value === undefined || value === null) return false;
    if (typeof value === "string" && value.trim() === "") return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });

  return Object.fromEntries(entries);
}

function normalizeFacetArray(value) {
  return Array.isArray(value)
    ? [
        ...new Set(
          value.map((item) => String(item || "").trim()).filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b))
    : [];
}

function normalizeFacets(facets = {}) {
  return {
    ...EMPTY_FACETS,
    colors: normalizeFacetArray(facets.colors),
    materials: normalizeFacetArray(facets.materials),
    sizes: normalizeFacetArray(facets.sizes),
    patterns: normalizeFacetArray(facets.patterns),
  };
}

function formatResultLabel(count) {
  const safeCount = Number(count || 0);
  return `${safeCount} ${safeCount === 1 ? "piece" : "pieces"}`;
}

function countActiveFilters(filters) {
  let total = 0;

  if (filters.minPrice || filters.maxPrice) total += 1;
  if (filters.inStock) total += 1;
  if (filters.sort && filters.sort !== "newest") total += 1;

  ["colors", "materials", "sizes", "patterns"].forEach((key) => {
    total += filters[key]?.length || 0;
  });

  return total;
}

function buildMetaDescription({ shopName, description, count }) {
  const countText = Number.isFinite(Number(count))
    ? ` ${Number(count)} items listed.`
    : "";

  return `Shop ${shopName}.${countText} ${description || ""}`.trim();
}

function buildPaginationModel(currentPage, totalPages) {
  const current = parsePositiveInt(currentPage, 1);
  const total = parsePositiveInt(totalPages, 1);

  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const model = [1];

  if (current > 3) model.push("start-ellipsis");

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let pageNumber = start; pageNumber <= end; pageNumber += 1) {
    model.push(pageNumber);
  }

  if (current < total - 2) model.push("end-ellipsis");

  model.push(total);

  return [...new Set(model)];
}

function normalizeFiltersFromSearchParams(searchParams) {
  return {
    category: normalizeIdParam(searchParams.get("category")),
    subCategory: normalizeIdParam(
      searchParams.get("subCategory") || searchParams.get("subcategory"),
    ),
    sort: normalizeSort(searchParams.get("sort")),
    minPrice: normalizePriceParam(searchParams.get("minPrice")),
    maxPrice: normalizePriceParam(searchParams.get("maxPrice")),
    inStock: searchParams.get("inStock") === "true",
    colors: parseCsvParam(searchParams.get("colors")),
    materials: parseCsvParam(searchParams.get("materials")),
    sizes: parseCsvParam(searchParams.get("sizes")),
    patterns: parseCsvParam(searchParams.get("patterns")),
    pageNumber: parsePositiveInt(searchParams.get("pageNumber"), 1),
  };
}

const ShopScreen = () => {
  const { shopKey = "" } = useParams();
  const normalizedShopKey = String(shopKey || "").trim();

  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const isValidShop = useMemo(
    () => isValidShopKey(normalizedShopKey),
    [normalizedShopKey],
  );

  const currentShop = useMemo(
    () => SHOPS.find((shop) => shop.shopKey === normalizedShopKey) || null,
    [normalizedShopKey],
  );

  const searchParamsKey = searchParams.toString();
  const mtmStep = useMemo(
    () => normalizeMtmStep(searchParams.get("mtmStep")),
    [searchParams],
  );
  const isMtmSelectionMode = Boolean(mtmStep);

  const currentFilters = useMemo(
    () => normalizeFiltersFromSearchParams(new URLSearchParams(searchParamsKey)),
    [searchParamsKey],
  );

  const activeFilterCount = useMemo(
    () => countActiveFilters(currentFilters),
    [currentFilters],
  );

  const shouldNoIndex = useMemo(
    () => activeFilterCount > 0 || currentFilters.pageNumber > 1,
    [activeFilterCount, currentFilters.pageNumber],
  );

  const productList = useSelector((state) => state.productList || {});

  const {
    loading = false,
    error = "",
    products = [],
    page = 1,
    pages = 1,
    count = 0,
    facets = EMPTY_FACETS,
  } = productList;

  const safeProducts = useMemo(
    () => (Array.isArray(products) ? products.filter(Boolean) : []),
    [products],
  );

  const safeFacets = useMemo(() => normalizeFacets(facets), [facets]);

  const serviceCatalogContext = useMemo(
    () =>
      getServiceCatalogContext({
        products: safeProducts,
        categoryId: currentFilters.category,
        subCategoryId: currentFilters.subCategory,
      }),
    [currentFilters.category, currentFilters.subCategory, safeProducts],
  );

  const serviceInfo = serviceCatalogContext.serviceInfo;
  const displayTitle =
    serviceCatalogContext.title || currentShop?.name || "Academy Costumes London";

  const requestParams = useMemo(
    () =>
      cleanQueryParams({
        shopKey: normalizedShopKey,
        category: currentFilters.category || undefined,
        subCategory: currentFilters.subCategory || undefined,
        sort: currentFilters.sort,
        minPrice: currentFilters.minPrice || undefined,
        maxPrice: currentFilters.maxPrice || undefined,

        inStock: currentFilters.inStock ? true : undefined,
        includeOutOfStock: currentFilters.inStock ? undefined : true,
        availability: currentFilters.inStock ? "in_stock" : "all",

        colors: buildCsvParam(currentFilters.colors),
        materials: buildCsvParam(currentFilters.materials),
        sizes: buildCsvParam(currentFilters.sizes),
        patterns: buildCsvParam(currentFilters.patterns),

        pageNumber: currentFilters.pageNumber,
        pageSize: PAGE_SIZE,
        mtmStep: mtmStep || undefined,
      }),
    [normalizedShopKey, currentFilters, mtmStep],
  );

  useEffect(() => {
    if (!isValidShop) return;

    dispatch(listProductsByShop(normalizedShopKey, requestParams));
  }, [dispatch, isValidShop, normalizedShopKey, requestParams]);

  const updateParams = useCallback(
    (updates = {}, { resetPage = false, replace = true } = {}) => {
      const next = new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        const normalizedValue = Array.isArray(value)
          ? buildCsvParam(value)
          : String(value ?? "").trim();

        if (
          value === undefined ||
          value === null ||
          normalizedValue === "" ||
          (key === "inStock" && normalizedValue === "false")
        ) {
          next.delete(key);
        } else {
          next.set(key, normalizedValue);
        }
      });

      if (resetPage) next.delete("pageNumber");

      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams],
  );

  const handleBulkFilterChange = useCallback(
    (updates = {}) => {
      updateParams(updates, { resetPage: true, replace: true });
    },
    [updateParams],
  );

  useEffect(() => {
    if (loading) return;
    if (currentFilters.pageNumber <= 1) return;

    const safeTotalPages = Math.max(1, Number(pages || 1));
    if (currentFilters.pageNumber <= safeTotalPages) return;

    updateParams(
      { pageNumber: safeTotalPages > 1 ? safeTotalPages : undefined },
      { replace: true },
    );
  }, [loading, currentFilters.pageNumber, pages, updateParams]);

  const handleFilterChange = useCallback(
    (key, value) => {
      updateParams({ [key]: value }, { resetPage: true, replace: true });
    },
    [updateParams],
  );

  const handleToggleFacetValue = useCallback(
    (facetKey, value) => {
      const existing = currentFilters[facetKey] || [];
      const hasValue = existing.includes(value);

      const nextValues = hasValue
        ? existing.filter((item) => item !== value)
        : [...existing, value];

      updateParams(
        { [facetKey]: nextValues },
        { resetPage: true, replace: true },
      );
    },
    [currentFilters, updateParams],
  );

  const clearAllFilters = useCallback(() => {
    const next = new URLSearchParams();
    if (currentFilters.category) next.set("category", currentFilters.category);
    if (currentFilters.subCategory) {
      next.set("subCategory", currentFilters.subCategory);
    }
    if (mtmStep) next.set("mtmStep", mtmStep);
    setSearchParams(next, { replace: true });
  }, [
    currentFilters.category,
    currentFilters.subCategory,
    mtmStep,
    setSearchParams,
  ]);

  const handlePageChange = useCallback(
    (nextPage) => {
      const parsed = parsePositiveInt(nextPage, 1);
      if (parsed === currentFilters.pageNumber) return;

      updateParams({ pageNumber: parsed }, { replace: false });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [currentFilters.pageNumber, updateParams],
  );

  const metaTitle = useMemo(() => {
    if (!currentShop) return "Shop | Academy Costumes London";

    const parts = [displayTitle];

    if (currentFilters.pageNumber > 1) {
      parts.push(`Page ${currentFilters.pageNumber}`);
    }

    parts.push("Academy Costumes London");
    return parts.join(" | ");
  }, [currentShop, currentFilters.pageNumber, displayTitle]);

  const metaDescription = useMemo(() => {
    if (!currentShop) {
      return "Explore premium fabrics and textiles from Academy Costumes London.";
    }

    return buildMetaDescription({
      shopName: currentShop.name,
      description: currentShop.description,
      count,
    });
  }, [currentShop, count]);

  const metaKeywords = useMemo(() => {
    if (!currentShop) {
      return "Academy Costumes London, shop, textiles, haberdashery, ready-made";
    }

    return [
      currentShop.name,
      normalizedShopKey,
      "Academy Costumes London",
      ...currentFilters.colors,
      ...currentFilters.materials,
      ...currentFilters.patterns,
    ]
      .filter(Boolean)
      .join(", ");
  }, [
    currentShop,
    normalizedShopKey,
    currentFilters.colors,
    currentFilters.materials,
    currentFilters.patterns,
  ]);

  const resultLabel = useMemo(() => formatResultLabel(count), [count]);

  const contextLabel = useMemo(() => {
    if (isMtmSelectionMode) {
      return MTM_STEP_LABELS[mtmStep] || "Made-to-measure selection";
    }
    if (currentFilters.inStock) return "In-stock selection";
    if (activeFilterCount > 0) return "Refined selection";
    return "Curated collection";
  }, [activeFilterCount, currentFilters.inStock, isMtmSelectionMode, mtmStep]);

  const paginationModel = useMemo(
    () => buildPaginationModel(page, pages),
    [page, pages],
  );

  const paginationItems = useMemo(
    () =>
      paginationModel.map((item, index) => {
        if (typeof item !== "number") {
          return <Pagination.Ellipsis key={`${item}-${index}`} disabled />;
        }

        return (
          <Pagination.Item
            key={item}
            active={item === page}
            onClick={() => handlePageChange(item)}
            linkClassName="shop-screen__page-link"
          >
            {item}
          </Pagination.Item>
        );
      }),
    [handlePageChange, page, paginationModel],
  );

  if (!isValidShop) {
    return (
      <main className="shop-screen py-4">
        <Meta
          title="Invalid Shop | Academy Costumes London"
          description="The requested shop could not be found."
          robots="noindex, follow"
          url={location.pathname}
          breadcrumbs={[
            { name: "Home", url: "/" },
            { name: "Invalid Shop", url: location.pathname },
          ]}
        />
        <Message variant="danger">Invalid shop selected.</Message>
      </main>
    );
  }

  return (
    <main className="shop-screen">
      <Meta
        title={metaTitle}
        description={metaDescription}
        keywords={metaKeywords}
        robots={shouldNoIndex ? "noindex, follow" : "index, follow"}
        url={location.pathname + location.search}
        products={safeProducts}
        breadcrumbs={[
          { name: "Home", url: "/" },
          {
            name: currentShop?.name || "Shop",
            url: `/shop/${normalizedShopKey}`,
          },
        ]}
      />

      <div className="shop-screen__inner">
        <section
          className={clsx("shop-screen__masthead", {
            "is-refined": activeFilterCount > 0,
          })}
        >
          <section className="shop-screen__hero">
            <div className="shop-screen__hero-top">
              <div className="shop-screen__hero-intro">
                <span className="shop-screen__eyebrow">Shop</span>
                <span className="shop-screen__context">{contextLabel}</span>
              </div>

              <span className="shop-screen__count-pill" aria-live="polite">
                {resultLabel}
              </span>
            </div>

            <div className="shop-screen__hero-main">
              <h1 className="shop-screen__title">
                {displayTitle}
              </h1>

              {serviceInfo ? (
                <aside
                  className="shop-screen__service-info"
                  aria-label={serviceInfo.heading || serviceInfo.title}
                >
                  {serviceInfo.heading ? (
                    <h2>{serviceInfo.heading}</h2>
                  ) : null}

                  {serviceInfo.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </aside>
              ) : currentShop?.description ? (
                <p className="shop-screen__description">
                  {currentShop.description}
                </p>
              ) : null}
            </div>
          </section>

          <div className="shop-screen__filters-shell">
            <LuxFilterBar
              filters={currentFilters}
              facets={safeFacets}
              onFilterChange={handleFilterChange}
              onBulkFilterChange={handleBulkFilterChange}
              onToggleFacetValue={handleToggleFacetValue}
              onClearAll={clearAllFilters}
            />
          </div>
        </section>

        {loading ? (
          <div className="shop-screen__state shop-screen__state--loading">
            <div className="shop-screen__state-card">
              <Loader />
            </div>
          </div>
        ) : error ? (
          <div className="shop-screen__state">
            <div className="shop-screen__state-card">
              <Message variant="danger">{error}</Message>
            </div>
          </div>
        ) : safeProducts.length === 0 ? (
          <div className="shop-screen__state shop-screen__state--empty">
            <div className="shop-screen__state-card">
              <Message variant="info">
                No products found for this shop with the current filters.
              </Message>
            </div>
          </div>
        ) : (
          <>
            <div
              className={clsx("shop-screen__grid", {
                "shop-screen__grid--dense": safeProducts.length > 8,
              })}
            >
              {safeProducts.map((product) => (
                <article
                  key={product._id}
                  className="shop-screen__product-shell"
                >
                  <ProductCard
                    product={product}
                    shopKey={normalizedShopKey}
                    mtmStep={mtmStep}
                    servicePricingContext={serviceInfo}
                  />
                </article>
              ))}
            </div>

            {pages > 1 ? (
              <nav
                className="shop-screen__pagination"
                aria-label="Shop pagination"
              >
                <Pagination>
                  <Pagination.Prev
                    aria-label="Previous page"
                    disabled={page <= 1}
                    onClick={() => handlePageChange(page - 1)}
                  />
                  {paginationItems}
                  <Pagination.Next
                    aria-label="Next page"
                    disabled={page >= pages}
                    onClick={() => handlePageChange(page + 1)}
                  />
                </Pagination>
              </nav>
            ) : null}
          </>
        )}
      </div>
    </main>
  );
};

export default ShopScreen;
